import React, { useState, useEffect, useRef } from 'react';
import { BarChart3 } from 'lucide-react';
import api from '../../services/api';
import PageHeader from '../../components/PageHeader';
import { useSelector } from 'react-redux';
import { io } from 'socket.io-client';
import { formatCurrency } from '../../utils/currency';
import { useCompanyCurrency } from '../../hooks/useCompanySettings';
import { PAGE } from '../../components/crm/ui';

const CARD_COLORS = {
  blue: 'bg-[#EAF4FE] border-[#B8DAF3] text-[#0176D3]',
  green: 'bg-[#E3F3E8] border-[#9FD9B4] text-[#036B3A]',
  purple: 'bg-[#EEF4FF] border-[#C9E2F8] text-[#0176D3]',
  orange: 'bg-[#FFF4E5] border-[#FFB75D] text-[#8C4B02]',
};

function StatCard({ label, value, sub, color = 'blue' }) {
  return (
    <div className={`border rounded-lg p-4 ${CARD_COLORS[color]}`}>
      <p className="text-xs font-medium opacity-70 mb-1">{label}</p>
      <p className="text-3xl font-bold">{value ?? '—'}</p>
      {sub && <p className="text-xs mt-1 opacity-60">{sub}</p>}
    </div>
  );
}

function BarChart({ data, labelKey, valueKey, color = '#3b82f6' }) {
  if (!data?.length) return <p className="text-[#706E6B] text-sm text-center py-8">No data</p>;
  const max = Math.max(...data.map(d => d[valueKey] || 0), 1);
  return (
    <div className="flex items-end gap-2 h-36 mt-2">
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1">
          <span className="text-xs text-[#706E6B]">{d[valueKey]}</span>
          <div className="w-full rounded-t-md" style={{ height: `${(d[valueKey] / max) * 100}px`, backgroundColor: color }} />
          <span className="text-xs text-[#706E6B] truncate max-w-full text-center">{d[labelKey]}</span>
        </div>
      ))}
    </div>
  );
}

function LineChart({ data, color = '#10b981' }) {
  if (!data?.length) return <p className="text-[#706E6B] text-sm text-center py-8">No data</p>;
  const max = Math.max(...data.map(d => d.count), 1);
  const W = 400, H = 100;
  const pts = data.map((d, i) => ({
    x: (i / (data.length - 1 || 1)) * W,
    y: H - (d.count / max) * H,
  }));
  const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ minWidth: 200, height: 100 }}>
        <path d={d} fill="none" stroke={color} strokeWidth="2" />
        {pts.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={3} fill={color} />)}
      </svg>
      <div className="flex justify-between text-xs text-[#706E6B] mt-1">
        <span>{data[0]?.date}</span>
        <span>{data[data.length - 1]?.date}</span>
      </div>
    </div>
  );
}

export default function AnalyticsPage() {
  const { user } = useSelector(s => s.auth);
  const companyCurrency = useCompanyCurrency();
  const [overview, setOverview] = useState(null);
  const [leadsByStage, setLeadsByStage] = useState([]);
  const [leadsTrend, setLeadsTrend] = useState([]);
  const [dealsPipeline, setDealsPipeline] = useState([]);
  const [adPerf, setAdPerf] = useState(null);
  const [inboxStats, setInboxStats] = useState(null);
  const [voiceStats, setVoiceStats] = useState([]);
  const [workflowStats, setWorkflowStats] = useState([]);
  const [live, setLive] = useState(null);
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [exporting, setExporting] = useState('');
  const socketRef = useRef(null);

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    // The live-counters cron broadcasts into each tenant's room on the
    // /inbox namespace (every staff client already connects there and
    // auto-joins on auth) — the default namespace has no such room to join.
    const token = localStorage.getItem('token');
    const socket = io('/inbox', { auth: { token }, transports: ['websocket'] });
    socketRef.current = socket;
    socket.on('analytics:live', data => setLive(data));
    return () => socket.disconnect();
  }, []);

  const load = async (params = {}) => {
    const q = new URLSearchParams(params).toString();
    const qs = q ? `?${q}` : '';
    const [ov, lbs, lt, dp, ap, is_, vs, ws, lv] = await Promise.all([
      api.get(`/analytics/overview${qs}`),
      api.get(`/analytics/leads/stage${qs}`),
      api.get(q ? `/analytics/leads/trend${qs}` : '/analytics/leads/trend?days=30'),
      api.get(`/analytics/deals/pipeline${qs}`),
      api.get(`/analytics/ads${qs}`),
      api.get(`/analytics/inbox${qs}`),
      api.get(`/analytics/voice${qs}`),
      api.get(`/analytics/workflow${qs}`),
      api.get('/analytics/live'),
    ]);
    setOverview(ov.data.data);
    setLeadsByStage(lbs.data.data || []);
    setLeadsTrend(lt.data.data || []);
    setDealsPipeline(dp.data.data || []);
    setAdPerf(ap.data.data);
    setInboxStats(is_.data.data);
    setVoiceStats(vs.data.data || []);
    setWorkflowStats(ws.data.data || []);
    setLive(lv.data.data);
  };

  const applyDateRange = () => load({ from: dateRange.from, to: dateRange.to });

  const handleExport = async (type) => {
    setExporting(type);
    try {
      const params = dateRange.from ? `?from=${dateRange.from}&to=${dateRange.to}` : '';
      const res = await api.get(`/analytics/export/${type}${params}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics.${{ excel: 'xlsx', csv: 'csv', pdf: 'pdf' }[type]}`;
      a.click();
      URL.revokeObjectURL(url);
    } finally { setExporting(''); }
  };

  return (
    <div className={PAGE}>
      {/* Header */}
      <PageHeader
        title="Analytics"
        subtitle="Real-time insights across your CRM"
        icon={BarChart3}
        actions={<>
          <input type="date" value={dateRange.from} onChange={e => setDateRange(d => ({ ...d, from: e.target.value }))}
            className="h-9 border border-[#D8DDE6] rounded px-3 text-[13px] focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]" />
          <span className="text-[#5C5C5C] text-[13px]">to</span>
          <input type="date" value={dateRange.to} onChange={e => setDateRange(d => ({ ...d, to: e.target.value }))}
            className="h-9 border border-[#D8DDE6] rounded px-3 text-[13px] focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]" />
          <button onClick={applyDateRange} className="h-9 px-4 bg-[#0176D3] hover:bg-[#014486] text-white rounded text-[13px] font-semibold whitespace-nowrap">Apply</button>
          <button onClick={() => handleExport('excel')} disabled={exporting === 'excel'}
            className="h-9 px-3 bg-white border border-[#C9C9C9] text-[#0176D3] hover:bg-[#F3F3F3] rounded text-[13px] font-semibold disabled:opacity-50 whitespace-nowrap">
            {exporting === 'excel' ? '...' : 'Excel'}
          </button>
          <button onClick={() => handleExport('csv')} disabled={exporting === 'csv'}
            className="h-9 px-3 bg-white border border-[#C9C9C9] text-[#0176D3] hover:bg-[#F3F3F3] rounded text-[13px] font-semibold disabled:opacity-50 whitespace-nowrap">
            {exporting === 'csv' ? '...' : 'CSV'}
          </button>
          <button onClick={() => handleExport('pdf')} disabled={exporting === 'pdf'}
            className="h-9 px-3 bg-white border border-[#C9C9C9] text-[#0176D3] hover:bg-[#F3F3F3] rounded text-[13px] font-semibold disabled:opacity-50 whitespace-nowrap">
            {exporting === 'pdf' ? '...' : 'PDF'}
          </button>
        </>}
      />

      {/* Live counters */}
      {live && (
        <div className="bg-[#0176D3] rounded-lg p-4 text-white flex flex-wrap gap-6">
          <div>
            <p className="text-xs opacity-70">Leads Today</p>
            <p className="text-3xl font-bold">{live.leadsToday}</p>
          </div>
          <div>
            <p className="text-xs opacity-70">Messages (last 1h)</p>
            <p className="text-3xl font-bold">{live.messagesThisHour}</p>
          </div>
          <div>
            <p className="text-xs opacity-70">Active Workflows</p>
            <p className="text-3xl font-bold">{live.activeRuns}</p>
          </div>
          <div className="ml-auto self-end text-xs opacity-50">
            Live · {live.timestamp ? new Date(live.timestamp).toLocaleTimeString() : ''}
          </div>
        </div>
      )}

      {/* Overview cards */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Leads" value={overview.leads.total} sub={`${overview.leads.new} new this period`} color="blue" />
          <StatCard label="Conversion Rate" value={`${overview.leads.conversionRate}%`} sub={`${overview.leads.converted} converted`} color="green" />
          <StatCard label="Deals Won" value={overview.deals.won} sub={`of ${overview.deals.total} total`} color="purple" />
          <StatCard label="Bot Deflection" value={`${overview.inbox.botDeflectionRate}%`} sub={`${overview.inbox.totalMessages} total messages`} color="orange" />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Leads by Stage */}
        <div className="bg-white rounded-lg border border-[#E5E5E5] p-5 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
          <h2 className="text-[14px] font-bold text-[#2E2E2E] mb-3">Leads by Stage</h2>
          <BarChart data={leadsByStage} labelKey="stage" valueKey="count" color="#3b82f6" />
        </div>

        {/* Leads Trend */}
        <div className="bg-white rounded-lg border border-[#E5E5E5] p-5 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
          <h2 className="text-[14px] font-bold text-[#2E2E2E] mb-3">Leads Trend (30 days)</h2>
          <LineChart data={leadsTrend} color="#10b981" />
        </div>

        {/* Deals Pipeline */}
        <div className="bg-white rounded-lg border border-[#E5E5E5] p-5 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
          <h2 className="text-[14px] font-bold text-[#2E2E2E] mb-3">Deals Pipeline</h2>
          <div className="space-y-2">
            {dealsPipeline.map((d, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-xs text-[#706E6B] w-24 truncate capitalize">{d.stage}</span>
                <div className="flex-1 bg-[#F3F2F2] rounded-full h-3">
                  <div className="bg-[#0176D3] h-3 rounded-full" style={{ width: `${Math.min((d.count / (dealsPipeline[0]?.count || 1)) * 100, 100)}%` }} />
                </div>
                <span className="text-xs text-[#444444] w-12 text-right">{d.count}</span>
                <span className="text-xs text-[#706E6B]">{formatCurrency(d.value, companyCurrency, { compact: true })}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Ads Performance */}
        <div className="bg-white rounded-lg border border-[#E5E5E5] p-5 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
          <h2 className="text-[14px] font-bold text-[#2E2E2E] mb-3">Ads Performance</h2>
          {adPerf && (
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Total Spend', value: formatCurrency(adPerf.summary.totalSpend, 'USD') },
                { label: 'Total Leads', value: adPerf.summary.totalLeads },
                { label: 'CPL', value: formatCurrency(adPerf.summary.cpl, 'USD') },
                { label: 'CTR', value: `${adPerf.summary.ctr}%` },
              ].map(({ label, value }) => (
                <div key={label} className="bg-[#F7F9FB] rounded-lg p-3">
                  <p className="text-xs text-[#706E6B]">{label}</p>
                  <p className="text-lg font-bold text-[#2E2E2E]">{value}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Inbox Stats */}
        <div className="bg-white rounded-lg border border-[#E5E5E5] p-5 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
          <h2 className="text-[14px] font-bold text-[#2E2E2E] mb-3">Inbox by Channel</h2>
          {inboxStats && (
            <>
              <div className="flex gap-4 mb-3">
                <div className="bg-[#EAF4FE] rounded-lg px-4 py-2">
                  <p className="text-xs text-[#0176D3]">Inbound</p>
                  <p className="text-lg font-bold text-[#0176D3]">{inboxStats.inbound}</p>
                </div>
                <div className="bg-[#E3F3E8] rounded-lg px-4 py-2">
                  <p className="text-xs text-[#04844B]">Outbound</p>
                  <p className="text-lg font-bold text-[#036B3A]">{inboxStats.outbound}</p>
                </div>
              </div>
              <BarChart data={inboxStats.byChannel || []} labelKey="channel" valueKey="count" color="#6366f1" />
            </>
          )}
        </div>

        {/* Voice Stats */}
        <div className="bg-white rounded-lg border border-[#E5E5E5] p-5 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
          <h2 className="text-[14px] font-bold text-[#2E2E2E] mb-3">Voice Calls</h2>
          <div className="space-y-2">
            {voiceStats.map((v, i) => (
              <div key={i} className="flex justify-between items-center text-sm border-b border-[#F3F2F2] pb-1">
                <span className="text-[#5C5C5C] capitalize">{v.direction} / {v.status}</span>
                <span className="font-medium text-[#2E2E2E]">{v.count}</span>
                <span className="text-xs text-[#706E6B]">{v.avgDuration}s avg</span>
              </div>
            ))}
            {voiceStats.length === 0 && <p className="text-[#706E6B] text-sm">No call data</p>}
          </div>
        </div>

        {/* Workflow Stats */}
        <div className="bg-white rounded-lg border border-[#E5E5E5] p-5 md:col-span-2 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
          <h2 className="text-[14px] font-bold text-[#2E2E2E] mb-3">Workflow Runs</h2>
          <div className="flex gap-4">
            {workflowStats.map((s, i) => (
              <div key={i} className={`rounded-lg px-5 py-3 ${s.status === 'completed' ? 'bg-[#E3F3E8] text-[#036B3A]' : s.status === 'failed' ? 'bg-[#FDE7E9] text-[#BA0517]' : 'bg-[#EAF4FE] text-[#0176D3]'}`}>
                <p className="text-xs capitalize">{s.status}</p>
                <p className="text-2xl font-bold">{s.count}</p>
              </div>
            ))}
            {workflowStats.length === 0 && <p className="text-[#706E6B] text-sm">No workflow runs</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
